// Closed domain value sets (data-model.md). Enforced here at the boundary; Wave 3
// mirrors each as a database CHECK/enum so an illegal value is both rejected at the
// edge and unrepresentable at rest (app-engineering §2). Widen a set only through the
// contract owner (contracts.md) and an ADR.
import { z } from "zod";
// --- Identity ---
export const MembershipRole = z.enum(["owner", "admin", "viewer"]);
export const Locale = z.enum(["en", "nl"]);
// --- Apps ---
export const AppType = z.enum(["existing", "imported", "new"]);
export const AppIntent = z.enum([
    "keep_alive",
    "get_live",
    "harden",
    "revamp",
    "migrate",
    "deploy",
]);
export const AppOrigin = z.enum(["lovable", "bolt", "replit", "v0", "cursor", "own_repo"]);
export const RepoHost = z.enum(["github", "gitlab"]);
export const AppStage = z.enum(["validated", "mvp", "production"]);
export const AppVerdict = z.enum(["passed", "conditional", "unvalidated", "none"]);
export const AppHost = z.enum(["managed", "production_cloud"]);
export const AppStatus = z.enum(["active", "parked", "offboarded"]);
export const CeilingSource = z.enum(["auto", "founder"]);
/** Nightcrew-owned, never a customer setting (data-model.md apps; D37). */
export const ShiftProfile = z.enum(["production", "mvp", "quiet"]);
/** Per-app autonomy (00-architecture §4, D50). The hard gate overrides all three. */
export const AutonomyLevel = z.enum(["suggest_only", "auto_stage", "auto_ship_trivial"]);
// --- Connections ---
export const ConnectionPlatform = z.enum([
    "github",
    "gitlab",
    "aws",
    "azure",
    "gcp",
    "scaleway",
    "ovh",
    "stripe",
    "email",
    "anthropic",
    "higgsfield",
]);
export const ConnectionMode = z.enum(["federated", "scoped_token"]);
/**
 * A customer cloud (data-model.md apps.cloud; ADR-0016). Where an app's production build runs.
 * NEW this slice with NO db pgEnum partner yet - its `cloud` pgEnum lands atomically in Wave 2
 * (packages/db), so `connectionPlatform` is deliberately NOT widened here (CR-002 lesson: a
 * mirrored-enum change is an atomic cross-package edit). Widen this set only through an ADR.
 */
export const Cloud = z.enum([
    "aws",
    "azure",
    "gcp",
    "scaleway",
    "ovh",
    "otc",
    "alibaba",
    "supabase",
    "vercel",
]);
export const ConnectionStatus = z.enum(["active", "needs_action", "revoked"]);
// --- Environments ---
export const EnvironmentType = z.enum(["local", "dev", "staging", "preproduction", "production"]);
// --- Secrets ---
export const SecretKind = z.enum(["api_key", "ssh_private_key", "deploy_key", "token"]);
export const SecretOrigin = z.enum(["founder_supplied", "crew_generated"]);
// --- Ledger ---
export const LedgerStage = z.enum(["triage", "research", "implement", "test", "review"]);
// --- Billing ---
export const PlanName = z.enum(["solo", "studio", "business"]);
// --- The shift: fixes ---
/** Internal only, never shown to the customer (data-model.md fixes.category). */
export const FixCategory = z.enum([
    "auto",
    "simple",
    "light",
    "standard",
    "heavy",
    "rework",
    "extreme",
]);
export const FixRisk = z.enum(["low", "medium", "high"]);
// --- Knowledge layer (ADR-0019 decision 2) ---
/**
 * A knowledge-layer risk severity for the ranked-risk map (knowledge-architecture.md:78,94;
 * ADR-0019 decision 2). Centralized so the ranked-risk surface and any later risk view share one
 * closed set. Deliberately DISTINCT from `FixRisk` (a fix's blast radius) even though the values
 * coincide: the two axes are different concepts and must not be merged.
 */
export const RiskLevel = z.enum(["low", "medium", "high"]);
// --- Audit ---
/**
 * Who took a privileged decision (data-model.md audit_log:105). `hard_gate` is the founder's
 * absolute gate (auth/payments/PII); `system` (CR-002) is the platform itself acting - a
 * kill-switch denial, a scheduler decision - distinct from a crew or a founder action. This
 * closed set is mirrored value-for-value by the DB pgEnum (packages/db/src/schema/enums.ts)
 * under a strict parity gate and an expand migration; keep the three in lockstep.
 */
export const AuditActor = z.enum(["crew", "founder", "hard_gate", "vault", "system"]);
// --- The shift: intake ---
/** The six intake lanes that create `intake_items` (data-model.md; shift-engine §6). */
export const IntakeLane = z.enum([
    "user_report",
    "crash",
    "voice",
    "proactive",
    "watch",
    "own_commit",
]);
/** How an intake item entered: active (user-driven), passive (crew-detected), or founder-supplied. */
export const SourceClass = z.enum(["active", "passive", "founder_supplied"]);
/** The lifecycle of an intake item (data-model.md intake_items.status). */
export const IntakeStatus = z.enum(["new", "triaged", "merged_into_fix", "dismissed"]);
/** Coarse sentiment on intake and feedback (data-model.md; nullable at the field). */
export const Sentiment = z.enum(["pos", "neu", "neg"]);
// --- The shift: the durable job queue (shift-engine §4) ---
/**
 * A durable job's state. `queued` -> `claimed` -> `completed`; a transient failure
 * returns it to `queued` (backoff) until attempts exhaust to `dead_letter` (which
 * raises an Escalated fix); `failed` is a terminal non-retryable failure.
 */
export const JobState = z.enum(["queued", "claimed", "completed", "failed", "dead_letter"]);
// --- The shift: the roster (shift-engine §2) ---
/** The shift kinds on the roster. Nightcrew-owned, never a customer setting (D37). */
export const ShiftKind = z.enum(["night", "day", "weekly", "watch", "oncall"]);
/** What started a shift: the roster, an on-call wake condition, or accumulated intake. */
export const ShiftTrigger = z.enum(["scheduled", "event", "threshold"]);
/**
 * A shift's run state. data-model.md leaves `shifts.status` un-enumerated; this closed
 * set (scheduled -> running -> completed, or aborted) is the Slice-1 modeling choice,
 * recorded in contracts.md for the Wave-3 db lane to mirror as a CHECK.
 */
export const ShiftStatus = z.enum(["scheduled", "running", "completed", "aborted"]);
// --- The shift: incidents (data-model.md incidents; shift-engine §2) ---
export const IncidentSeverity = z.enum(["sev1", "sev2", "sev3"]);
export const IncidentStatus = z.enum(["detected", "mitigating", "mitigated", "resolved"]);
export const IncidentCause = z.enum(["crew_change", "customer_change", "external", "security"]);
// --- Feedback (data-model.md feedback_submissions) ---
export const FeedbackKind = z.enum(["bug", "feature", "vote", "survey", "voice"]);
export const FeedbackSource = z.enum(["widget", "email", "import", "founder_note"]);
// --- Billing inbox (Slice 6; slice-plan.md:86; ADR-0023 decision 6; D27 - never a fix/action/token) ---
/**
 * How a raw billing-inbox item entered the system (mirrors `FeedbackSource` above): a live
 * provider connector, a founder upload (a CSV/PDF export), a typed manual entry, or a forwarded
 * invoice email. Widen only through an ADR.
 */
export const BillingSource = z.enum(["connector", "upload", "manual_entry", "forwarded_email"]);
/**
 * The kind of a raw billing-inbox item (ADR-0023 decision 1: "the founder's own cloud/infra +
 * third-party services + the flat Nightcrew plan price"). Shares its value set with
 * `BillingCategory` (model/money-view.ts) by design - kept as a separate named type because a raw
 * inbox item and an aggregated all-in-cost line are different concepts, even though today they
 * resolve 1:1. NEVER a fix/action/token kind (D27).
 */
export const BillingItemKind = z.enum(["cloud", "services", "nightcrew_plan"]);
//# sourceMappingURL=enums.js.map